import React, { useCallback, useEffect, useRef, useState } from 'react';

import { apiDeleteAsUser, apiGetAsUser, apiPatchAsUser, apiPostAsUser, responseErrorMessage } from '../../../lib/api_v2';
import { logError } from '../../../lib/logger';

import { SettingsGroup, SettingsToggle } from '../../shared/SettingsList';

const PREFERENCE_CONSENT =
  'Enable this linked account for this workspace? This saves your connection preference. Gmail and Calendar features are not available yet, and enabling this account does not share its contents or authorize public publishing.';

const ConnectionToggle = ({ connection, busy, onToggle, onAuthorize, onDisconnect }) => {
  const handleChange = useCallback(
    function handleChange(enabled) {
      onToggle(connection, enabled);
    },
    [connection, onToggle],
  );

  const description =
    connection.status === 'active'
      ? connection.widget_access ? accessDescription(connection) : 'Save this account for use in this workspace'
      : connection.status === 'provider_unavailable'
        ? 'Service currently unavailable'
        : 'Reconnect to restore access';

  return (
    <div>
      <SettingsToggle
        label={`${connection.title} — ${connection.account_label}`}
        description={description}
        checked={connection.enabled}
        disabled={busy || (!connection.enabled && connection.status !== 'active')}
        onChange={handleChange}
      />
      <div className="flex flex-wrap gap-2 px-4 pb-4">
        <button type="button" className={BUTTON} data-provider={connection.provider} data-connection-id={connection.id}
          onClick={onAuthorize} disabled={busy || connection.status === 'provider_unavailable'}>
          Reconnect
        </button>
        <button type="button" className={BUTTON} data-connection-id={connection.id} data-account-label={connection.account_label}
          onClick={onDisconnect} disabled={busy}>
          Disconnect
        </button>
      </div>
    </div>
  );
};

const BUTTON = 'cursor-pointer rounded-full bg-gray-200 px-3 py-2 text-cursed disabled:cursor-not-allowed disabled:opacity-50';

const ProviderRow = ({ provider, busy, onAuthorize }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-cursed">{provider.title}</span>
    <button type="button" className={BUTTON} data-provider={provider.key} onClick={onAuthorize} disabled={!provider.configured || busy}>
      {provider.configured ? `Connect ${provider.title}` : 'Unavailable'}
    </button>
  </div>
);

const SharedConnectionRow = ({ connection }) => (
  <div className="flex items-center justify-between gap-3 text-cursed">
    <span>{connection.member.full_name} · {connection.provider} · {connection.account.label}</span>
    <span className="text-gray-500">{connection.status === 'needs_reauth' ? 'Needs reconnecting' : 'Connected'}</span>
  </div>
);

function accessDescription(connection) {
  if (connection.provider === 'github' || connection.key === 'github') return 'GitHub App permissions, including changes allowed by your authorization';
  return (connection.scopes || []).join(', ') || 'The access granted at the provider';
}

function sharingConsent(connection, workspaceTitle) {
  return `Allow widgets and their authoring assistants in ${workspaceTitle} to use your ${connection.title} account? Access: ${accessDescription(connection)}. Data retrieved this way can be shown to ${workspaceTitle} members. Any member can use the assistant to make changes permitted by this authorization.`;
}

const ConnectionsSettings = ({ workspaceId }) => {
  const [connections, setConnections] = useState(null);
  const [error, setError] = useState('');
  const [sharedConnections, setSharedConnections] = useState([]);
  const [providers, setProviders] = useState([]);
  const [workspaceTitle, setWorkspaceTitle] = useState('this workspace');
  const [busyId, setBusyId] = useState(null);
  const loadSequence = useRef(0);

  const load = useCallback(
    async function loadWorkspaceConnections() {
      if (!workspaceId) return;
      const sequence = ++loadSequence.current;
      setConnections(null);
      setError('');
      setBusyId(null);
      try {
        const response = await apiGetAsUser(`/workspaces/${encodeURIComponent(workspaceId)}/connections`);
        const data = await response.json();
        if (!response.ok || data.ok !== true) throw new Error(responseErrorMessage(data, 'Could not load connections.'));
        if (sequence === loadSequence.current) {
          setConnections(Array.isArray(data.data?.connections) ? data.data.connections : []);
          setSharedConnections(Array.isArray(data.data?.shared_connections) ? data.data.shared_connections : []);
          setProviders(Array.isArray(data.data?.providers) ? data.data.providers : []);
          setWorkspaceTitle(data.data?.workspace_title || 'this workspace');
        }
      } catch (loadError) {
        logError('ConnectionsSettings', 'Failed to load workspace connections', loadError);
        if (sequence === loadSequence.current) setError('Could not load connections.');
      }
    },
    [workspaceId],
  );

  useEffect(() => {
    load();
    return () => {
      loadSequence.current += 1;
    };
  }, [load]);

  const toggle = useCallback(
    async function toggleConnection(connection, enabled) {
      if (enabled && !window.confirm(connection.widget_access ? sharingConsent(connection, workspaceTitle) : PREFERENCE_CONSENT)) return;
      const sequence = loadSequence.current;
      setBusyId(connection.id);
      setError('');
      try {
        const path = `/workspaces/${encodeURIComponent(workspaceId)}/connections/${encodeURIComponent(connection.id)}`;
        const response = enabled ? await apiPatchAsUser(path) : await apiDeleteAsUser(path);
        const data = await response.json();
        if (!response.ok || data.ok !== true) throw new Error(responseErrorMessage(data, 'Could not update connection.'));
        if (sequence === loadSequence.current) {
          await load();
        }
      } catch (toggleError) {
        logError('ConnectionsSettings', 'Failed to toggle workspace connection', toggleError);
        if (sequence === loadSequence.current) setError('Could not update connection.');
      } finally {
        if (sequence === loadSequence.current) setBusyId(null);
      }
    },
    [workspaceId, workspaceTitle, load],
  );

  const authorize = useCallback(
    async function authorizeConnection(event) {
      const { provider: providerKey, connectionId } = event.currentTarget.dataset;
      const connection = connections.find((item) => item.id === connectionId);
      const provider = providers.find((item) => item.key === providerKey);
      if (!provider && !connection) return;
      const consent = connection
        ? `Reconnect ${connection.account_label} and review its permissions? Existing workspace sharing settings will be kept. Shared workspaces can use the newly granted permissions.`
        : sharingConsent(provider, workspaceTitle);
      if (!window.confirm(consent)) return;
      setBusyId(connectionId || providerKey);
      setError('');
      try {
        const response = await apiPostAsUser('/account/connection_authorizations', {
          provider: providerKey, client: 'web',
          ...(connectionId ? { connection_id: connectionId } : { workspace_id: workspaceId }),
        });
        const data = await response.json();
        if (!response.ok || data.ok !== true || !data.data?.authorization_url) {
          throw new Error(responseErrorMessage(data, 'Could not start authorization.'));
        }
        window.location.assign(data.data.authorization_url);
      } catch (authorizationError) {
        logError('ConnectionsSettings', 'Failed to start connection authorization', authorizationError);
        setError('Could not start authorization.');
        setBusyId(null);
      }
    },
    [connections, providers, workspaceId, workspaceTitle],
  );

  const disconnect = useCallback(
    async function disconnectConnection(event) {
      const { connectionId, accountLabel } = event.currentTarget.dataset;
      if (!window.confirm(`Disconnect ${accountLabel} from all workspaces? Widgets and assistants will lose access to this account. To disable only this workspace, use the toggle instead.`)) return;
      const sequence = loadSequence.current;
      setBusyId(connectionId);
      setError('');
      try {
        const response = await apiDeleteAsUser(`/account/connections/${encodeURIComponent(connectionId)}`);
        const data = await response.json();
        if (!response.ok || data.ok !== true) throw new Error(responseErrorMessage(data, 'Could not disconnect account.'));
        if (sequence === loadSequence.current) await load();
      } catch (disconnectError) {
        logError('ConnectionsSettings', 'Failed to disconnect account', disconnectError);
        if (sequence === loadSequence.current) setError('Could not disconnect account.');
      } finally {
        if (sequence === loadSequence.current) setBusyId(null);
      }
    },
    [load],
  );

  if (!connections && !error) return <p className="pt-6 text-cursed text-gray-500">Loading connections…</p>;
  if (!connections) {
    return (
      <div className="flex flex-col items-start gap-3 pt-6">
        <p className="text-cursed text-red-600">{error}</p>
        <button type="button" className="cursor-pointer rounded-full bg-gray-200 px-3 py-2 text-cursed" onClick={load}>
          Retry
        </button>
      </div>
    );
  }

  const unconnectedProviders = providers.filter(
    (provider) => !connections.some((connection) => connection.provider === provider.key && connection.status === 'active'),
  );

  return (
    <div className="flex w-full flex-col gap-4 pt-6">
      <p className="text-cursed text-gray-500">
        Enable your accounts so workspace widgets and their authoring assistants can use them. Each member controls their own accounts.
        Gmail and Calendar settings still only save a preference.
      </p>
      {error && <p className="text-cursed text-red-600">{error}</p>}
      <div className="flex flex-col gap-3">
        {unconnectedProviders.map((provider) => (
          <ProviderRow key={provider.key} provider={provider} busy={busyId !== null} onAuthorize={authorize} />
        ))}
      </div>
      {connections.length === 0 && (
        <p className="text-cursed text-gray-500">Connect an account above, or manage all your accounts in Your Profile → Connected services.</p>
      )}
      <h3 className="text-sm font-medium">Your connected accounts</h3>
      <SettingsGroup>
        {connections.map((connection) => (
          <ConnectionToggle key={connection.id} connection={connection} busy={busyId !== null} onToggle={toggle} onAuthorize={authorize} onDisconnect={disconnect} />
        ))}
      </SettingsGroup>
      <h3 className="text-sm font-medium">Available to workspace widgets</h3>
      {sharedConnections.length === 0 && <p className="text-cursed text-gray-500">No accounts enabled yet.</p>}
      {sharedConnections.map((connection) => (
        <SharedConnectionRow key={connection.id} connection={connection} />
      ))}
    </div>
  );
};

export default ConnectionsSettings;
